import { Badge, Box, Button, Card, Dialog, DialogActions, Typography } from '@mui/material'
import { styled } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react'
import api from '../services/api';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreateRelatorioEspontaneo from './createRelatorioEspontaneo';
import { Avatar } from '@mui/material';
import ShortNome from '../utils/ShortNome';
import DateHora from '../utils/dateHora';
import TextFormatado from '../utils/TextFormatado';
import HeaderGestorConfigs from '../utils/headerGestorConfigs';
import Loading from '../load/loading';

const ADD_RELATORIO_LABEL = 'Adicionar Relatório';
const PRIMARY_BUTTON_STYLE = { background: '#85287e' };

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
}));

interface RelatorioItem {
    _id: string;
    nome?: string;
    descricao?: string;
    anexo?: string;
    updatedAt?: string;
    admin?: {
        dadosPessoais?: {
            nome?: string;
            fotoURL?: string;
        };
    };
}

export default function Relatorios({ processoId, modelName }: { processoId: string, modelName: string }) {
    // const definicoesSistema = useSelector((state) => state.definicoesSistema.data);
    const [modal, setModal] = useState(false);
    const [addRelatorio, setAddRelatorio] = useState(false);
    const [loading, setLoading] = useState(false);
    const [relatorios, setRelatorios] = useState<RelatorioItem[]>([]);
    const [expanded, setExpanded] = React.useState<string | false>(false);

    const handleChange =
        (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    const handleClose = () => {
        setModal(false);
        setAddRelatorio(false);
        setExpanded(false);
    }

    const handleOpenModal = () => setModal(true);
    const handleOpenCreateRelatorio = () => setAddRelatorio(true);

    // relatorios
    useEffect(() => {
        setLoading(true);
        api.get('/relatorios?processoId=' + processoId)
            .then(res => {
                setRelatorios(res.data.relatorios || []);
            }).catch(() => {
                setRelatorios([]);
            }).finally(() => {
                setLoading(false);
            })
    }, [modal, addRelatorio, processoId]);

    const relatoriosOrdenados = useMemo(() => {
        return [...relatorios].sort((a, b) => {
            const dataA = a?.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            const dataB = b?.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            return dataB - dataA;
        });
    }, [relatorios]);

    return (
        // relatorios.length > 0 &&
        // <VerifyPermission permissions={definicoesSistema?.servicos } permission={'solicitarNumeroDeImportador'}>
        <>
            <Card
                sx={{
                    p: 2,
                    mb: 2,
                    cursor: 'pointer',
                    borderRadius: 3,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 6,
                    },
                }}
                onClick={handleOpenModal}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            Relatórios Espontâneos
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Consulte os relatórios anexados ao processo e adicione novos quando necessário.
                        </Typography>
                    </Box>

                    <Badge
                        sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}
                        badgeContent={relatorios.length}
                        color="error"
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <Button color="secondary">Ver Relatórios</Button>
                    </Badge>
                </Box>
            </Card>

            <BootstrapDialog
                scroll='body'
                maxWidth={'lg'}
                fullWidth
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={!!modal}
                sx={{
                    '& .MuiDialog-paper': {
                        width: '100%',
                        maxWidth: { xs: 'calc(100% - 24px)', md: '1100px' },
                        maxHeight: '90vh',
                        padding: { xs: 2, md: 4 },
                        margin: { xs: 1.5, md: 3 },
                        borderRadius: 3,
                    }
                }}
            >
                <HeaderGestorConfigs title="Relatórios Espontâneos" />
                {addRelatorio ?
                    <CreateRelatorioEspontaneo
                        processoId={processoId}
                        modelName={modelName}
                        handleClose={handleClose} />
                    :
                    <>
                        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                            <Box>
                                <Typography variant="h6" fontWeight={700}>
                                    Histórico de relatórios
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {relatorios.length > 0
                                        ? `${relatorios.length} relatório(s) disponível(is) para este processo.`
                                        : 'Ainda não existem relatórios espontâneos cadastrados para este processo.'}
                                </Typography>
                            </Box>

                            {relatorios.length > 0 && (
                                <Button
                                    variant="contained"
                                    style={PRIMARY_BUTTON_STYLE}
                                    onClick={handleOpenCreateRelatorio}
                                >
                                    {ADD_RELATORIO_LABEL}
                                </Button>
                            )}
                        </Box>

                        {loading && <Loading text={'Buscando Relatórios'} focus={false} />}

                        {!loading && relatoriosOrdenados.length === 0 && (
                            <Box
                                sx={{
                                    border: '1px dashed rgba(0, 0, 0, 0.18)',
                                    borderRadius: 3,
                                    p: 4,
                                    textAlign: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                }}
                            >
                                <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                                    Nenhum relatório disponível
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Adicione o primeiro relatório espontâneo para começar o histórico deste processo.
                                </Typography>
                                <Button
                                    variant="contained"
                                    style={PRIMARY_BUTTON_STYLE}
                                    onClick={handleOpenCreateRelatorio}
                                >
                                    {ADD_RELATORIO_LABEL}
                                </Button>
                            </Box>
                        )}

                        {!loading && relatoriosOrdenados.map((relatorio, index) => (

                            <Accordion
                                key={relatorio._id}
                                expanded={expanded === relatorio?._id}
                                onChange={handleChange(relatorio?._id)}
                                sx={{ mb: 1.5, borderRadius: '12px !important', overflow: 'hidden', '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="panel1bh-content"
                                    id="panel1bh-header"
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            width: { xs: '100%', md: '42%' },
                                            flexShrink: 0,
                                            pr: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                                                {index + 1}º
                                            </Typography>
                                            <Avatar src={relatorio.admin?.dadosPessoais?.fotoURL} style={{ marginRight: 5 }} />
                                            <Typography lineHeight={1.2} noWrap variant="body1">
                                                <ShortNome nome={relatorio.admin?.dadosPessoais?.nome} />
                                                <br />
                                                <small><DateHora date={relatorio?.updatedAt} /></small>
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography component="span" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                                        {relatorio?.nome}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>

                                    <Box sx={{ backgroundColor: 'rgba(0, 0, 0, 0.03)', borderRadius: '4px', mb: 4 }}>
                                        <TextFormatado texto={relatorio?.descricao ?? ''} />
                                    </Box>

                                    <embed
                                        id="pdfIframerelatorioEsposta"
                                        title={`PDF Viewer`}
                                        src={relatorio?.anexo}
                                        width="100%"
                                        height="680px"
                                        style={{ border: 'none' }}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        ))}

                        <DialogActions sx={{ p: 0, pt: 3, justifyContent: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
                            <Button variant="text" color="inherit" onClick={handleClose}>
                                Fechar
                            </Button>

                        </DialogActions>
                    </>
                }

            </BootstrapDialog>
        </>

        // </VerifyPermission> 
    )
}
